'use client';

import { useState } from 'react';

import type { PostMediaItem } from '@ps/api/types';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { Radio } from 'lucide-react';

interface Props {
  media: PostMediaItem;
  alt?: string;
  className?: string;
}

/**
 * `true` when the current browser can't play `application/vnd.apple.mpegurl`
 * natively. Safari (macOS/iOS) returns "maybe"/"probably"; everything else `""`.
 * Falls back to `true` (show the badge) when probing isn't possible.
 */
function detectRequiresHls(): boolean {
  if (typeof document === 'undefined') return true;
  const probe = document.createElement('video');
  return probe.canPlayType('application/vnd.apple.mpegurl') === '';
}

/**
 * LIVE_REPLAY branch. HLS isn't natively supported by Chrome/Firefox and there's
 * no HLS lib bundled yet — Safari plays the `<video>` inline; other browsers show
 * the poster + a "Live Replay" badge (open in the app to watch). When hls.js
 * ports, the non-Safari branch becomes a `next/dynamic` import here. `requiresHls`
 * is computed once at mount so non-Safari never briefly mounts the `<video>`.
 */
export function PostLiveReplayMedia({ media, alt, className }: Props) {
  const [requiresHls] = useState(() => detectRequiresHls());

  if (requiresHls) {
    return (
      <div
        className={`absolute inset-0 flex items-center justify-center bg-black ${className ?? ''}`}
      >
        {media.thumbnailUrl ? (
          <PopImage
            src={media.thumbnailUrl}
            alt={alt ?? media.caption ?? 'Live replay'}
            fill
            resizeMode="cover"
            sizes="(min-width: 1024px) 540px, 100vw"
          />
        ) : null}
        <div className="bg-scrim/40 pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-white">
            <div className="flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide">
              <Radio className="h-3 w-3" aria-hidden />
              Live Replay
            </div>
            <p className="text-[12px] opacity-90">
              Open in the POP.STORE app to watch
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Safari path — native HLS. A replay still processing has no `url`; show the
  // poster-only thumbnail without mounting an empty-`src` `<video>`.
  if (!media.url) {
    return media.thumbnailUrl ? (
      <PopImage
        src={media.thumbnailUrl}
        alt={alt ?? media.caption ?? ''}
        fill
        resizeMode="cover"
        className={className}
        sizes="(min-width: 1024px) 540px, 100vw"
      />
    ) : (
      <div
        className={`bg-scrim/60 absolute inset-0 h-full w-full ${className ?? ''}`}
        aria-label={alt ?? media.caption ?? 'Live replay'}
      />
    );
  }

  return (
    <video
      preload="metadata"
      controls
      playsInline
      poster={media.thumbnailUrl}
      src={media.url}
      aria-label={alt ?? media.caption ?? 'Live replay'}
      className={`absolute inset-0 h-full w-full ${className ?? ''}`}
    />
  );
}
